import React from 'react';
import axios from 'axios';
import { useLookupData } from './hooks/useLookupData';
import DataDisplay from './components/DataDisplay';
import StatusBadge from './components/StatusBadge';
import { UI_CONSTANTS, API_ENDPOINTS } from './constants';
import { CustomerData, InvoiceData, PaymentData } from './types';
import { addLogEntry } from './utils/devTabLogger';

interface CrawlFilters {
  ignoreArchivedInvoices: boolean;
  ignoreUnsentInvoices: boolean;
}

interface LookupRowProps {
  type: 'customer' | 'invoice' | 'payment';
  stepNumber: number;
  items?: any[];
  onIdClick?: (id: string, targetType: 'invoice' | 'payment' | 'customer') => void;
  externalSearchId?: string;
  onSearchIdChange?: (id: string) => void;
  crawlFilters?: CrawlFilters;
  onCrawlFiltersChange?: (filters: CrawlFilters) => void;
}

const LookupRow: React.FC<LookupRowProps> = ({ type, stepNumber, items = [], onIdClick, externalSearchId, onSearchIdChange, crawlFilters, onCrawlFiltersChange }) => {
  const { loading, qboLoading, searchId, searchedData, qboData, setSearchId, handleLookup, handleBack, handleRefresh, handleNext, handleLookupRefresh, handleCrawl, handleStopCrawl, isCrawling, config, lookupRefreshText, showBackButton, status, statusText } = useLookupData({ type, items, externalSearchId, onSearchIdChange, crawlFilters });
  const [fixLoading, setFixLoading] = React.useState(false);
  
  const hasInputValue = searchId.trim().length > 0;
  const isLoading = loading || fixLoading;

  return (
    <tr>
      <td className="border border-gray-300 px-3 py-2 font-medium">{stepNumber}. {config.label}</td>
      <td className="border border-gray-300 px-3 py-2 max-h-48 overflow-y-auto">
        <div className="flex flex-col gap-2">
          <div className="flex gap-2 items-center">
            <input
              type="text"
              placeholder={config.placeholder}
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className={`px-3 py-1 border border-gray-300 rounded text-sm ${UI_CONSTANTS.INPUT_WIDTH}`}
            />
            <div className="flex flex-col gap-1 items-end">
              {/* Back button - now at top */}
              {showBackButton && (
                <button
                  onClick={handleBack}
                  className={`px-2 py-0.5 border ${config.borderColor} ${config.textColor} text-xs rounded disabled:opacity-50 flex items-center gap-0.5 hover:bg-gray-50`}
                  disabled={isLoading}
                  title="Previous"
                >
                  Back
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              )}
              
              {/* Lookup/Refresh button - always dark gray */}
              <button
                onClick={handleLookupRefresh}
                className="px-2 py-0.5 border border-gray-600 text-gray-600 text-xs rounded disabled:opacity-50 flex items-center gap-0.5 hover:bg-gray-50"
                disabled={isLoading}
                title={hasInputValue ? "Lookup by ID" : "Refresh to max ID"}
              >
                {isLoading ? '...' : (
                  <>
                    {lookupRefreshText}
                    {hasInputValue ? (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    )}
                  </>
                )}
              </button>
              
              {/* Crawl button - for all types (customer increments, invoice/payment decrements) */}
          <button
            onClick={isCrawling ? handleStopCrawl : handleCrawl}
            className={`px-2 py-0.5 border ${isCrawling ? 'border-red-600 text-red-600 hover:bg-red-50' : type === 'customer' ? 'border-purple-600 text-purple-600 hover:bg-purple-50' : type === 'invoice' ? 'border-blue-600 text-blue-600 hover:bg-blue-50' : 'border-green-600 text-green-600 hover:bg-green-50'} text-xs rounded disabled:opacity-50 flex items-center gap-0.5`}
            disabled={isLoading}
            title={isCrawling 
              ? "Stop crawling" 
              : type === 'customer' 
                ? "Crawl forward to next customer with sync issues" 
                : type === 'invoice'
                  ? "Crawl backward to previous invoice with sync issues"
                  : "Crawl backward to previous payment with sync issues"}
          >
            {isCrawling ? (
              <>
                Stop
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </>
            ) : (
              <>
                Crawl
                {type === 'customer' ? (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 17l-4-4m0 0l-4 4m4-4v12" />
                  </svg>
                )}
              </>
            )}
          </button>
          
          {/* Next button */}
          <button
            onClick={handleNext}
            className={`px-2 py-0.5 border ${config.borderColor} ${config.textColor} text-xs rounded disabled:opacity-50 flex items-center gap-0.5 hover:bg-gray-50`}
            disabled={isLoading}
            title="Next"
          >
            {isLoading ? '...' : (
              <>
                Next
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </>
            )}
          </button>
            </div>
          </div>
          
                      {/* Crawl Filter Checkboxes - for customers, invoices, and payments */}
            {crawlFilters && onCrawlFiltersChange && (
              <div className="flex flex-col gap-1 mt-1 pt-2 border-t border-gray-200">
                <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={crawlFilters.ignoreArchivedInvoices}
                    onChange={(e) => onCrawlFiltersChange({ ...crawlFilters, ignoreArchivedInvoices: e.target.checked })}
                    className="w-3.5 h-3.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span>Ignore archived invoices in crawl</span>
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={crawlFilters.ignoreUnsentInvoices}
                    onChange={(e) => onCrawlFiltersChange({ ...crawlFilters, ignoreUnsentInvoices: e.target.checked })}
                    className="w-3.5 h-3.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <span>Ignore unsent invoices in crawl</span>
                </label>
              </div>
            )}
        </div>
      </td>
      <td className={`border border-gray-300 px-3 py-2 ${UI_CONSTANTS.MAX_DATA_WIDTH} max-h-48 overflow-y-auto`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          </div>
        ) : searchedData && (
          <div className="font-mono text-xs">
            {searchedData.error ? (
              <div className="text-red-600 bg-red-50 p-2 rounded">
                <strong>Error:</strong> {searchedData.error}
              </div>
            ) : (
              <>
                <DataDisplay data={searchedData} type={type} />
              </>
            )}
          </div>
        )}
      </td>
      <td className={`border border-gray-300 px-3 py-2 ${UI_CONSTANTS.MAX_DATA_WIDTH} max-h-48 overflow-y-auto`}>
        {isLoading || qboLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          </div>
        ) : qboData ? (
          (() => {
            // Check if customer has unsynced associated items even though customer exists
            let hasUnsyncedItems = false;
            const unsyncedDetails: string[] = [];
            
            if (type === 'customer' && searchedData) {
              const customerData = searchedData as CustomerData;
              const ignoreArchived = crawlFilters?.ignoreArchivedInvoices || false;
              const ignoreUnsent = crawlFilters?.ignoreUnsentInvoices || false;
              const invoicesArchiveStatus = customerData.invoices_archive_status || {};
              const invoicesSentStatus = customerData.invoices_sent_status || {};
              
              // Count unsynced invoices, optionally excluding archived/unsent ones
              const unsyncedInvoices = Object.entries(customerData.invoices_sync_status || {})
                .filter(([invoiceId, synced]) => {
                  if (synced === true) return false; // Synced invoices don't count
                  // If ignoreArchived is enabled and invoice is archived, don't count it
                  if (ignoreArchived && invoicesArchiveStatus[parseInt(invoiceId)]) return false;
                  // If ignoreUnsent is enabled and invoice is not sent, don't count it
                  if (ignoreUnsent && !invoicesSentStatus[parseInt(invoiceId)]) return false;
                  return synced === false; // Count unsynced
                }).length;
              
              const unsyncedPayments = Object.entries(customerData.payments_sync_status || {}).filter(([_, synced]) => synced === false).length;
              
              if (unsyncedInvoices > 0) {
                hasUnsyncedItems = true;
                const archivedCount = ignoreArchived ? Object.entries(customerData.invoices_sync_status || {})
                  .filter(([invoiceId, synced]) => synced === false && invoicesArchiveStatus[parseInt(invoiceId)]).length : 0;
                const unsentCount = ignoreUnsent ? Object.entries(customerData.invoices_sync_status || {})
                  .filter(([invoiceId, synced]) => synced === false && !invoicesSentStatus[parseInt(invoiceId)]).length : 0;
                const ignoredParts = [];
                if (archivedCount > 0) ignoredParts.push(`${archivedCount} archived`);
                if (unsentCount > 0) ignoredParts.push(`${unsentCount} unsent`);
                const detailText = ignoredParts.length > 0 
                  ? `${unsyncedInvoices} invoice(s) not in QBO${ignoredParts.length > 0 ? ` (${ignoredParts.join(', ')} ignored)` : ''}`
                  : `${unsyncedInvoices} invoice(s) not in QBO`;
                unsyncedDetails.push(detailText);
              }
              if (unsyncedPayments > 0) {
                hasUnsyncedItems = true;
                unsyncedDetails.push(`${unsyncedPayments} payment(s) not in QBO`);
              }
            }
            
            // Check if QBO lookup failed with an error
            if (qboData.found === false && qboData.error) {
              const isRateLimit = (qboData as any).rateLimit === true;
              return (
                <div className="font-mono text-xs">
                  <div className={`p-2 rounded border ${isRateLimit ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center gap-1 mb-1">
                      {isRateLimit ? (
                        <svg className="w-3 h-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      )}
                      <span className={`text-xs font-medium ${isRateLimit ? 'text-orange-700' : 'text-red-700'}`}>
                        {isRateLimit ? 'Rate Limit' : 'QBO Error'}
                      </span>
                    </div>
                    <div className={`text-xs ${isRateLimit ? 'text-orange-600' : 'text-red-600'}`}>
                      {qboData.error}
                    </div>
                  </div>
                </div>
              );
            }
            
            // QBO data found - show normal display
            return (
              <div className="font-mono text-xs">
                <div className={`p-2 rounded border ${hasUnsyncedItems ? 'bg-yellow-50 border-yellow-200' : (qboData.warnings && qboData.warnings.length > 0) ? 'bg-orange-50 border-orange-200' : 'bg-green-50'}`}>
                  <div>ID: {qboData.id || 'N/A'}</div>
                  {type === 'customer' && (
                    <>
                      <div>Name: {qboData.data?.name || 'N/A'}</div>
                      <div>Company: {qboData.data?.companyName || 'N/A'}</div>
                    </>
                  )}
                  {type === 'invoice' && (
                    <>
                      <div>Doc Number: {qboData.data?.docNumber || 'N/A'}</div>
                      <div>Total: ${qboData.data?.totalAmt || 0}</div>
                      <div>Balance: ${qboData.data?.balance || 0}</div>
                      <div>Date: {qboData.data?.txnDate || 'N/A'}</div>
                      <div className={qboData.data?.isCustomerDeleted ? 'text-red-600 font-medium' : ''}>
                        Customer: {qboData.data?.customerRef || 'N/A'}
                      </div>
                      {qboData.warnings && qboData.warnings.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-red-300">
                          <div className="flex items-center gap-1 mb-1">
                            <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span className="text-red-700 font-medium text-xs">Warning:</span>
                          </div>
                          {qboData.warnings.map((warning: any, idx: number) => (
                            <div key={idx} className="text-red-600 text-xs">
                              • {warning.message}
                            </div>
                          ))}
                          <div className="text-xs text-red-500 mt-1 italic">
                            This invoice may not be visible in QuickBooks due to the inactive customer.
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {hasUnsyncedItems && (
                    <div className="mt-2 pt-2 border-t border-yellow-300">
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          const itemId = type === 'payment' ? (searchedData as any).payment_id : (searchedData as any).id;
                          
                          // Get the list of items that need fixing
                          const customerData = searchedData as CustomerData;
                          const ignoreArchived = crawlFilters?.ignoreArchivedInvoices || false;
                          const ignoreUnsent = crawlFilters?.ignoreUnsentInvoices || false;
                          const invoicesArchiveStatus = customerData.invoices_archive_status || {};
                          const invoicesSentStatus = customerData.invoices_sent_status || {};
                          
                          const unsyncedInvoiceIds = Object.entries(customerData.invoices_sync_status || {})
                            .filter(([invoiceId, synced]) => {
                              if (synced === true) return false;
                              // If ignoreArchived is enabled and invoice is archived, exclude it from fix list
                              if (ignoreArchived && invoicesArchiveStatus[parseInt(invoiceId)]) return false;
                              // If ignoreUnsent is enabled and invoice is not sent, exclude it from fix list
                              if (ignoreUnsent && !invoicesSentStatus[parseInt(invoiceId)]) return false;
                              return synced === false;
                            })
                            .map(([id, _]) => parseInt(id));
                          const unsyncedPaymentIds = Object.entries(customerData.payments_sync_status || {})
                            .filter(([_, synced]) => synced === false)
                            .map(([id, _]) => parseInt(id));
                          
                          // Show confirm dialog with details
                          const confirmMessage = `Fix associated items for this ${type}?\n\n${unsyncedDetails.join('\n')}`;
                          if (!window.confirm(confirmMessage)) {
                            return;
                          }
                          
                          setFixLoading(true);
                          try {
                            console.log('🔧 Fix Associated Items button clicked for', type, itemId);
                            console.log('🔧 Unsynced invoices:', unsyncedInvoiceIds);
                            console.log('🔧 Unsynced payments:', unsyncedPaymentIds);
                            
                            const fixUrl = API_ENDPOINTS.FIX_QBO(type, itemId);
                            console.log('🔧 Making fix QBO request to:', fixUrl);
                            
                            const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}${fixUrl}`, {
                              unsyncedInvoiceIds,
                              unsyncedPaymentIds
                            });
                            const result = response.data;
                            
                            addLogEntry({
                              action: 'fix_qbo_associated',
                              type: type,
                              id: itemId.toString(),
                              success: result.success || false,
                              message: result.message,
                              error: result.error
                            });
                            
                            if (result.success) {
                              console.log('✅ QBO fix successful:', result);
                              handleLookupRefresh();
                            } else {
                              console.error('❌ QBO fix failed:', result);
                            }
                          } catch (error) {
                            console.error('❌ QBO fix error:', error);
                            addLogEntry({
                              action: 'fix_qbo_associated',
                              type: type,
                              id: itemId.toString(),
                              success: false,
                              error: error.message
                            });
                          } finally {
                            setFixLoading(false);
                          }
                        }}
                        className="w-full px-2 py-0.5 text-xs border border-yellow-600 text-yellow-700 rounded hover:bg-yellow-100 transition-colors flex items-center justify-center gap-1"
                        title={unsyncedDetails.join(', ')}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        Fix Associated Items
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        ) : searchedData && !searchedData.error ? (
          (() => {
            // Check if any associated items need fixing
            let hasUnsyncedItems = false;
            const unsyncedDetails: string[] = [];
            
            if (type === 'customer') {
              const customerData = searchedData as CustomerData;
              const unsyncedInvoices = Object.entries(customerData.invoices_sync_status || {}).filter(([_, synced]) => synced === false).length;
              const unsyncedPayments = Object.entries(customerData.payments_sync_status || {}).filter(([_, synced]) => synced === false).length;
              
              if (unsyncedInvoices > 0) {
                hasUnsyncedItems = true;
                unsyncedDetails.push(`${unsyncedInvoices} invoice(s) not in QBO`);
              }
              if (unsyncedPayments > 0) {
                hasUnsyncedItems = true;
                unsyncedDetails.push(`${unsyncedPayments} payment(s) not in QBO`);
              }
            }
            
            return (
              <div className="font-mono text-xs">
                <div className="bg-red-50 p-2 rounded border border-red-200">
                  <div className="text-red-700 mb-2">
                    <div className="flex items-center gap-1 mb-1">
                      <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-xs font-medium">QBO {hasUnsyncedItems ? 'Sync Issues' : 'Data Missing'}</span>
                    </div>
                    <div className="text-xs text-red-600">
                      {type === 'customer' && !hasUnsyncedItems && 'Customer not found in QuickBooks'}
                      {type === 'invoice' && 'Invoice not found in QuickBooks'}
                      {type === 'payment' && 'Payment not found in QuickBooks'}
                      {hasUnsyncedItems && unsyncedDetails.map((detail, idx) => (
                        <div key={idx}>• {detail}</div>
                      ))}
                    </div>
                  </div>
              <button
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  const itemId = type === 'payment' ? (searchedData as any).payment_id : (searchedData as any).id;
                  setFixLoading(true);
                  try {
                    console.log('🔧 Fix QBO button clicked for', type, itemId);
                    console.log('🔧 SearchedData:', searchedData);
                    console.log('🔧 ItemId extracted:', itemId);
                    
                    const fixUrl = API_ENDPOINTS.FIX_QBO(type, itemId);
                    console.log('🔧 Making fix QBO request to:', fixUrl);
                    
                    const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}${fixUrl}`);
                    
                    console.log('🔧 Fix QBO response status:', response.status);
                    console.log('🔧 Fix QBO response data:', response.data);
                    
                    const result = response.data;
                    
                    // Log the action
                    addLogEntry({
                      action: 'fix_qbo',
                      type: type,
                      id: itemId.toString(),
                      success: result.success || false,
                      message: result.message,
                      qboId: result.data?.qboId || result.qboId,
                      error: result.error
                    });
                    
                    if (result.success) {
                      console.log('✅ QBO fix successful:', result);
                      // Refresh the current data to show updated QBO info
                      handleLookupRefresh();
                    } else {
                      console.error('❌ QBO fix failed:', result);
                      console.error(`Failed to fix QBO data: ${result.message || result.error}`);
                    }
                  } catch (error) {
                    console.error('❌ QBO fix error:', error);
                    console.error(`Error fixing QBO data: ${error.message}`);
                    
                    // Log the error
                    addLogEntry({
                      action: 'fix_qbo',
                      type: type,
                      id: itemId.toString(),
                      success: false,
                      error: error.message
                    });
                  } finally {
                    setFixLoading(false);
                  }
                }}
                className="w-full px-2 py-0.5 text-xs border border-red-600 text-red-600 rounded hover:bg-red-50 transition-colors"
                title={`Fix ${type} QBO data`}
              >
                Fix QBO
              </button>
                </div>
              </div>
            );
          })()
        ) : (
          <div className="font-mono text-xs text-gray-500">N/A</div>
        )}
      </td>
      <td className="border border-gray-300 px-3 py-2 max-h-48 overflow-y-auto">
        <div className="font-mono text-xs text-gray-600">
          {isLoading ? (
            <div className="text-gray-500">Searching...</div>
          ) : searchedData ? (
            searchedData.error ? (
              <div className="text-red-600">
                <div>Error occurred</div>
                <div>Check EServ column</div>
              </div>
            ) : (
              <>
                <div>Found: {type}</div>
                <div>ID: {type === 'payment' ? (searchedData as any).payment_id || 'N/A' : (searchedData as any).id || 'N/A'}</div>
                <div>Status: Success</div>
              </>
            )
          ) : (
            <div className="text-gray-500">Ready to search</div>
          )}
        </div>
      </td>
      <td className="border border-gray-300 px-2 py-2 text-center max-h-48 overflow-y-auto">
        <StatusBadge status={status} text={statusText} />
      </td>
      <td className="border border-gray-300 px-3 py-2 align-top" style={{ maxHeight: '12rem' }}>
        <div className="overflow-y-auto" style={{ maxHeight: '11rem' }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          </div>
        ) : (
          <>
        {/* Customer Row - Show associated invoices and payments */}
        {type === 'customer' && searchedData && 'associated_invoices' in searchedData && (
          (() => {
            const customerData = searchedData as CustomerData;
            const hasInvoices = customerData.associated_invoices && customerData.associated_invoices.length > 0;
            const hasPayments = customerData.associated_payments && customerData.associated_payments.length > 0;
            
            if (!hasInvoices && !hasPayments) {
              return <div className="text-gray-500 text-xs">-</div>;
            }
            
            return (
              <div className="font-mono text-xs">
                {hasInvoices && (
                  <div className="mb-2">
                    <div className="text-xs text-gray-500 mb-1 sticky top-0 bg-white">Invoices:</div>
                    <div className="max-w-full">
                      <div className="flex gap-1 flex-wrap">
                        {customerData.associated_invoices!
                          .sort((a, b) => {
                            const aStatus = customerData.invoices_sync_status?.[a];
                            const bStatus = customerData.invoices_sync_status?.[b];
                            // Sort: false (error) first, then undefined, then true (success)
                            if (aStatus === false && bStatus !== false) return -1;
                            if (bStatus === false && aStatus !== false) return 1;
                            if (aStatus === undefined && bStatus === true) return -1;
                            if (bStatus === undefined && aStatus === true) return 1;
                            return 0;
                          })
                          .map((invoiceId: number) => {
                          const isInQbo = customerData.invoices_sync_status?.[invoiceId];
                          const isArchived = customerData.invoices_archive_status?.[invoiceId];
                          const isSent = customerData.invoices_sent_status?.[invoiceId];
                          const bgColor = isInQbo === true ? 'bg-green-100 text-green-700 hover:bg-green-200' : 
                                         isInQbo === false ? 'bg-red-100 text-red-700 hover:bg-red-200' : 
                                         'bg-blue-100 text-blue-700 hover:bg-blue-200';
                          
                          // Build tooltip with all statuses
                          const tooltipParts = [];
                          if (isInQbo === true) tooltipParts.push('✓ Synced to QBO');
                          if (isInQbo === false) tooltipParts.push('✗ NOT in QBO');
                          if (isInQbo === undefined) tooltipParts.push('? Sync status unknown');
                          if (isArchived) tooltipParts.push('📦 Archived');
                          if (isSent) tooltipParts.push('✉️ Sent');
                          if (!isSent) tooltipParts.push('📬 Not sent');
                          
                          return (
                            <button
                              key={invoiceId}
                              onClick={() => onIdClick?.(invoiceId.toString(), 'invoice')}
                              className={`px-1.5 py-0.5 text-xs rounded transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-0.5 ${bgColor}`}
                              title={tooltipParts.join(', ')}
                            >
                              {isInQbo === true && (
                                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {isInQbo === false && (
                                <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              {isInQbo === undefined && (
                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                              <span>{invoiceId}</span>
                              {/* Archive status icon */}
                              {isArchived && (
                                <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                              )}
                              {/* Sent status icon */}
                              {isSent ? (
                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              ) : (
                                <svg className="w-3 h-3 text-gray-400 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
                {hasPayments && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1 sticky top-0 bg-white">Payments:</div>
                    <div className="max-w-full">
                      <div className="flex gap-1 flex-wrap">
                        {customerData.associated_payments!
                          .sort((a, b) => {
                            const aStatus = customerData.payments_sync_status?.[a];
                            const bStatus = customerData.payments_sync_status?.[b];
                            // Sort: false (error) first, then undefined, then true (success)
                            if (aStatus === false && bStatus !== false) return -1;
                            if (bStatus === false && aStatus !== false) return 1;
                            if (aStatus === undefined && bStatus === true) return -1;
                            if (bStatus === undefined && aStatus === true) return 1;
                            return 0;
                          })
                          .map((paymentId: number) => {
                          const isInQbo = customerData.payments_sync_status?.[paymentId];
                          const applicationIssues = customerData.payment_application_issues?.[paymentId];
                          const hasIssues = applicationIssues && applicationIssues.length > 0;
                          
                          const bgColor = isInQbo === true ? 'bg-green-100 text-green-700 hover:bg-green-200' : 
                                         isInQbo === false ? 'bg-red-100 text-red-700 hover:bg-red-200' : 
                                         'bg-green-100 text-green-700 hover:bg-green-200';
                          
                          const tooltipText = isInQbo === true 
                            ? (hasIssues ? `⚠️ Application issues:\n${applicationIssues.join('\n')}` : '✓ Synced to QBO and properly applied')
                            : isInQbo === false 
                              ? '✗ NOT in QBO' 
                              : '? Sync status unknown';
                          
                          return (
                            <button
                              key={paymentId}
                              onClick={() => onIdClick?.(paymentId.toString(), 'payment')}
                              className={`px-1.5 py-0.5 text-xs rounded transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-1 ${bgColor}`}
                              title={tooltipText}
                            >
                              {isInQbo === true && (
                                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {isInQbo === false && (
                                <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              {isInQbo === undefined && (
                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                              {paymentId}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}
        
        {/* Invoice Row - Show customer ID and associated payments */}
        {type === 'invoice' && searchedData && !searchedData.error && (
          (() => {
            const invoiceData = searchedData as InvoiceData;
            const hasCustomer = invoiceData.fk_bcustomer_id;
            const hasPayments = invoiceData.associated_payments && invoiceData.associated_payments.length > 0;
            
            if (!hasCustomer && !hasPayments) {
              return <div className="text-gray-500 text-xs">-</div>;
            }
            
            return (
              <div className="font-mono text-xs">
                {hasCustomer && (
                  <div className="mb-2">
                    <div className="text-xs text-gray-500 mb-1 sticky top-0 bg-white">Customer:</div>
                    {(() => {
                      const customerQboId = invoiceData.customer_qbo_id;
                      const isInQbo = customerQboId ? invoiceData.customer_sync_status?.[customerQboId] : undefined;
                      const bgColor = isInQbo === true ? 'bg-green-100 text-green-700 hover:bg-green-200' : 
                                     isInQbo === false ? 'bg-red-100 text-red-700 hover:bg-red-200' : 
                                     'bg-purple-100 text-purple-700 hover:bg-purple-200';
                      return (
                        <button
                          onClick={() => onIdClick?.(invoiceData.fk_bcustomer_id!.toString(), 'customer')}
                          className={`px-1.5 py-0.5 text-xs rounded transition-colors whitespace-nowrap flex items-center gap-1 ${bgColor}`}
                          title={isInQbo === true ? '✓ Synced to QBO' : isInQbo === false ? '✗ NOT in QBO' : '? Sync status unknown'}
                        >
                          {isInQbo === true && (
                            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {isInQbo === false && (
                            <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                          {isInQbo === undefined && (
                            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          {invoiceData.fk_bcustomer_id}
                        </button>
                      );
                    })()}
                  </div>
                )}
                {hasPayments && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1 sticky top-0 bg-white">Payments:</div>
                    <div className="overflow-x-auto max-w-full">
                      <div className="flex gap-1 flex-wrap">
                        {invoiceData.associated_payments!.map((paymentId: number) => (
                          <button
                            key={paymentId}
                            onClick={() => onIdClick?.(paymentId.toString(), 'payment')}
                            className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors whitespace-nowrap flex-shrink-0"
                          >
                            {paymentId}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}
        
        {/* Payment Row - Show customer ID and associated invoices */}
        {type === 'payment' && searchedData && !searchedData.error && (
          (() => {
            const paymentData = searchedData as PaymentData;
            const hasCustomer = paymentData.fk_bcustomer_id;
            const hasInvoices = paymentData.associated_invoices && paymentData.associated_invoices.length > 0;
            
            if (!hasCustomer && !hasInvoices) {
              return <div className="text-gray-500 text-xs">-</div>;
            }
            
            return (
              <div className="font-mono text-xs">
                {hasCustomer && (
                  <div className="mb-2">
                    <div className="text-xs text-gray-500 mb-1 sticky top-0 bg-white">Customer:</div>
                    <button
                      onClick={() => onIdClick?.(paymentData.fk_bcustomer_id!.toString(), 'customer')}
                      className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors whitespace-nowrap"
                    >
                      {paymentData.fk_bcustomer_id}
                    </button>
                  </div>
                )}
                {hasInvoices && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1 sticky top-0 bg-white">Invoices:</div>
                    <div className="overflow-x-auto max-w-full">
                      <div className="flex gap-1 flex-wrap">
                        {paymentData.associated_invoices!.map((invoiceId: number) => (
                          <button
                            key={invoiceId}
                            onClick={() => onIdClick?.(invoiceId.toString(), 'invoice')}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors whitespace-nowrap flex-shrink-0"
                          >
                            {invoiceId}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}
          </>
        )}
        </div>
      </td>
      <td className="border border-gray-300 px-3 py-2 align-top" style={{ maxHeight: '12rem' }}>
        <div className="overflow-y-auto" style={{ maxHeight: '11rem' }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
          </div>
        ) : (
          <div className="text-xs">
            {(() => {
              const issues: string[] = [];
              
              // Check for customer issues
              if (type === 'customer' && searchedData && qboData) {
                if (!qboData.found) {
                  issues.push('❌ Customer not found in QBO');
                }
                
                const customerData = searchedData as CustomerData;
                const invoicesSyncStatus = customerData.invoices_sync_status || {};
                const invoicesArchiveStatus = customerData.invoices_archive_status || {};
                const invoicesSentStatus = customerData.invoices_sent_status || {};
                const ignoreArchived = crawlFilters?.ignoreArchivedInvoices || false;
                const ignoreUnsent = crawlFilters?.ignoreUnsentInvoices || false;
                
                // Categorize ALL unsynced invoices (including undefined status which means not checked)
                // Show all mismatches in issues, regardless of filter settings
                // Filters only affect whether crawl stops, not whether issues are shown
                const unsyncedInvoices = Object.entries(invoicesSyncStatus)
                  .filter(([_, synced]) => synced === false || synced === undefined)
                  .map(([id, synced]) => {
                    const invoiceId = parseInt(id);
                    const isArchived = invoicesArchiveStatus[invoiceId] || false;
                    const isSent = invoicesSentStatus[invoiceId] || false;
                    const isChecked = synced !== undefined;
                    const shouldIgnoreForCrawl = (ignoreArchived && isArchived) || (ignoreUnsent && !isSent);
                    return { id: invoiceId, isArchived, isSent, isChecked, shouldIgnoreForCrawl };
                  });
                
                if (unsyncedInvoices.length > 0) {
                  // Separate invoices into categories for better reporting
                  const sentAndUnsynced = unsyncedInvoices.filter(inv => inv.isSent);
                  const archivedAndUnsynced = unsyncedInvoices.filter(inv => inv.isArchived);
                  const unsentAndUnsynced = unsyncedInvoices.filter(inv => !inv.isSent);
                  const uncheckedInvoices = unsyncedInvoices.filter(inv => !inv.isChecked);
                  
                  // Always show sent invoices that aren't synced (these are real issues)
                  if (sentAndUnsynced.length > 0) {
                    const filteredOut = sentAndUnsynced.filter(inv => inv.shouldIgnoreForCrawl).length;
                    const willStopCrawl = sentAndUnsynced.filter(inv => !inv.shouldIgnoreForCrawl).length;
                    issues.push(`📄 ${sentAndUnsynced.length} sent invoice(s) not in QBO${filteredOut > 0 ? ` (${willStopCrawl} will stop crawl, ${filteredOut} ignored)` : ''}:`);
                    sentAndUnsynced.slice(0, 5).forEach(inv => {
                      const ignoreNote = inv.shouldIgnoreForCrawl ? ' [ignored in crawl]' : '';
                      const statusNote = inv.isChecked ? '(sent, not synced)' : '(sent, sync status unknown)';
                      issues.push(`   • Invoice ${inv.id} ${statusNote}${ignoreNote}`);
                    });
                    if (sentAndUnsynced.length > 5) {
                      issues.push(`   ... and ${sentAndUnsynced.length - 5} more sent invoices`);
                    }
                  }
                  
                  // Always show archived invoices that aren't synced (but note if being ignored)
                  if (archivedAndUnsynced.length > 0) {
                    const filteredOut = archivedAndUnsynced.filter(inv => inv.shouldIgnoreForCrawl).length;
                    const willStopCrawl = archivedAndUnsynced.filter(inv => !inv.shouldIgnoreForCrawl).length;
                    const ignoreNote = filteredOut > 0 ? ` (${willStopCrawl} will stop crawl, ${filteredOut} ignored)` : '';
                    issues.push(`📦 ${archivedAndUnsynced.length} archived invoice(s) not in QBO${ignoreNote}:`);
                    archivedAndUnsynced.slice(0, 5).forEach(inv => {
                      const ignoreNote = inv.shouldIgnoreForCrawl ? ' [ignored in crawl]' : '';
                      const statusNote = inv.isChecked ? '(archived, not synced)' : '(archived, sync status unknown)';
                      issues.push(`   • Invoice ${inv.id} ${statusNote}${ignoreNote}`);
                    });
                    if (archivedAndUnsynced.length > 5) {
                      issues.push(`   ... and ${archivedAndUnsynced.length - 5} more archived invoices`);
                    }
                  }
                  
                  // Always show unsent invoices that aren't synced (but note if being ignored)
                  if (unsentAndUnsynced.length > 0) {
                    const filteredOut = unsentAndUnsynced.filter(inv => inv.shouldIgnoreForCrawl).length;
                    const willStopCrawl = unsentAndUnsynced.filter(inv => !inv.shouldIgnoreForCrawl).length;
                    const ignoreNote = filteredOut > 0 ? ` (${willStopCrawl} will stop crawl, ${filteredOut} ignored)` : '';
                    issues.push(`📬 ${unsentAndUnsynced.length} unsent invoice(s) not in QBO${ignoreNote}:`);
                    unsentAndUnsynced.slice(0, 5).forEach(inv => {
                      const ignoreNote = inv.shouldIgnoreForCrawl ? ' [ignored in crawl]' : '';
                      const statusNote = inv.isChecked ? '(unsent, not synced)' : '(unsent, sync status unknown)';
                      issues.push(`   • Invoice ${inv.id} ${statusNote}${ignoreNote}`);
                    });
                    if (unsentAndUnsynced.length > 5) {
                      issues.push(`   ... and ${unsentAndUnsynced.length - 5} more unsent invoices`);
                    }
                  }
                  
                  // Show unchecked invoices (invoices that weren't checked yet)
                  if (uncheckedInvoices.length > 0 && uncheckedInvoices.length !== unsyncedInvoices.length) {
                    issues.push(`❓ ${uncheckedInvoices.length} invoice(s) with unknown sync status (may need to refresh with checkAll=true)`);
                  }
                }
                
                // Check payment issues
                const unsyncedPayments = Object.entries(customerData.payments_sync_status || {})
                  .filter(([_, synced]) => synced === false);
                if (unsyncedPayments.length > 0) {
                  issues.push(`💵 ${unsyncedPayments.length} payment(s) not synced:`);
                  unsyncedPayments.slice(0, 5).forEach(([id, _]) => {
                    issues.push(`   • Payment ${id}`);
                  });
                  if (unsyncedPayments.length > 5) {
                    issues.push(`   ... and ${unsyncedPayments.length - 5} more`);
                  }
                }
                
                // Check payment application issues
                const applicationIssues = customerData.payment_application_issues || {};
                const paymentsWithIssues = Object.entries(applicationIssues);
                if (paymentsWithIssues.length > 0) {
                  issues.push(`⚠️ ${paymentsWithIssues.length} payment(s) with application issues:`);
                  paymentsWithIssues.forEach(([paymentId, issueList]) => {
                    issues.push(`   • Payment ${paymentId}:`);
                    issueList.forEach(issue => {
                      issues.push(`     - ${issue}`);
                    });
                  });
                }
              }
              
              // Check for invoice issues
              if (type === 'invoice' && searchedData && qboData) {
                const invoiceData = searchedData as InvoiceData;
                const invoiceSynced = qboData?.found === true;
                const customerQboId = invoiceData.customer_qbo_id;
                const customerSynced = customerQboId ? (invoiceData.customer_sync_status?.[customerQboId] === true) : undefined;
                
                if (!invoiceSynced) {
                  // Determine if invoice is sent (status >= 33) based on status label
                  const isSent = invoiceData.status?.label && (
                    invoiceData.status.label.toLowerCase().includes('sent') || 
                    invoiceData.status.label.toLowerCase().includes('paid') ||
                    invoiceData.status.label.toLowerCase().includes('partial')
                  );
                  
                  if (isSent) {
                    issues.push('❌ Invoice not found in QBO (sent, not synced)');
                  } else {
                    issues.push('❌ Invoice not found in QBO (not synced)');
                  }
                } else if (customerQboId && customerSynced === false) {
                  issues.push('⚠️ Invoice found in QBO, but customer not found in QBO');
                }
                
                if (qboData.warnings) {
                  qboData.warnings.forEach((warning: any) => {
                    issues.push(`⚠️ ${warning.message}`);
                  });
                }
              }
              
              // Check for payment issues
              if (type === 'payment' && searchedData && qboData) {
                const paymentSynced = qboData?.found === true;
                
                if (!paymentSynced) {
                  issues.push('❌ Payment not found in QBO (not synced)');
                }
              }
              
              if (issues.length === 0) {
                return <div className="text-green-600 font-medium">✓ All Clear</div>;
              }
              
              return (
                <div className="text-red-600 space-y-1">
                  {issues.map((issue, idx) => (
                    <div key={idx} className={issue.startsWith('   ') ? 'pl-2' : issue.startsWith('     ') ? 'pl-4' : ''}>
                      {issue}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
        </div>
      </td>
    </tr>
  );
};

export default LookupRow;
